import React, { useState, useEffect } from 'react';
import { useAppSettings } from '../context/AppSettingsContext';
import { meetingAPI } from '../services/api';
import './SuperUserPanel.css';

const SuperUserPanel = ({ userEmail }) => {
  const { settings, updateSetting, resetSettings, isSuperUser } = useAppSettings();
  const [isOpen, setIsOpen] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: '--',
    activeMeetings: '--',
    totalRecordings: '--',
    totalMeetings: '--'
  });
  const [loading, setLoading] = useState(false);

  // Fetch stats when panel opens
  useEffect(() => {
    if (isOpen && isSuperUser(userEmail)) {
      fetchStats();
    }
  }, [isOpen, userEmail]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      // Get meetings list to calculate stats
      const response = await meetingAPI.list();
      const meetings = response.data.meetings || [];
      
      const activeMeetings = meetings.filter(m => m.status === 'active').length;
      const totalMeetings = meetings.length;
      
      setStats(prev => ({
        ...prev,
        activeMeetings: activeMeetings.toString(),
        totalMeetings: totalMeetings.toString(),
      }));
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
    setLoading(false);
  };

  const handleToggle = (key) => {
    updateSetting(key, !settings[key]);
  };

  const handleClearCache = () => {
    if (window.confirm('Clear all cached data? This will refresh the app.')) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
    }
  };

  const handleResetSettings = () => {
    if (window.confirm('Reset all settings to default?')) {
      resetSettings();
    }
  };

  if (!isSuperUser(userEmail)) {
    return null;
  }

  return (
    <>
      <button 
        className="super-user-toggle"
        onClick={() => setIsOpen(!isOpen)}
        title="Super User Controls"
      >
        ⚙️ Admin
      </button>

      {isOpen && (
        <div className="super-user-overlay" onClick={() => setIsOpen(false)}>
          <div className="super-user-panel" onClick={(e) => e.stopPropagation()}>
            <div className="panel-header">
              <h2>👑 Super User Panel</h2>
              <button className="close-btn" onClick={() => setIsOpen(false)}>✕</button>
            </div>

            <div className="panel-content">
              <section className="panel-section">
                <h3>🎛️ Feature Controls</h3>
                
                <label className="toggle-item">
                  <span>Hide Registration Button</span>
                  <input
                    type="checkbox"
                    checked={settings.hideRegistration}
                    onChange={() => handleToggle('hideRegistration')}
                  />
                </label>

                <label className="toggle-item">
                  <span>Maintenance Mode</span>
                  <input
                    type="checkbox"
                    checked={settings.maintenanceMode}
                    onChange={() => handleToggle('maintenanceMode')}
                  />
                </label>

                <label className="toggle-item">
                  <span>Allow Recording</span>
                  <input
                    type="checkbox"
                    checked={settings.allowRecording}
                    onChange={() => handleToggle('allowRecording')}
                  />
                </label>

                <label className="toggle-item">
                  <span>Allow Guest Join</span>
                  <input
                    type="checkbox"
                    checked={settings.allowGuestJoin}
                    onChange={() => handleToggle('allowGuestJoin')}
                  />
                </label>

                <label className="toggle-item">
                  <span>Require Waiting Room</span>
                  <input
                    type="checkbox"
                    checked={settings.requireWaitingRoom}
                    onChange={() => handleToggle('requireWaitingRoom')}
                  />
                </label>

                <label className="toggle-item">
                  <span>Show Debug Info</span>
                  <input
                    type="checkbox"
                    checked={settings.showDebugInfo}
                    onChange={() => handleToggle('showDebugInfo')}
                  />
                </label>

                <label className="toggle-item">
                  <span>Beta Features</span>
                  <input
                    type="checkbox"
                    checked={settings.betaFeatures}
                    onChange={() => handleToggle('betaFeatures')}
                  />
                </label>
              </section>

              <section className="panel-section">
                <h3>📊 App Statistics</h3>
                {loading ? (
                  <p>Loading stats...</p>
                ) : (
                  <div className="stats-grid">
                    <div className="stat-item">
                      <span className="stat-value">{stats.activeMeetings}</span>
                      <span className="stat-label">Active Meetings</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{stats.totalMeetings}</span>
                      <span className="stat-label">Total Meetings</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{stats.totalRecordings}</span>
                      <span className="stat-label">Total Recordings</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">1.0.0</span>
                      <span className="stat-label">App Version</span>
                    </div>
                  </div>
                )}
              </section>

              <section className="panel-section">
                <h3>🔧 Quick Actions</h3>
                <div className="action-buttons">
                  <button className="action-btn" onClick={fetchStats}>
                    🔄 Refresh Stats
                  </button>
                  <button className="action-btn" onClick={handleClearCache}>
                    🗑️ Clear Cache
                  </button>
                  <button className="action-btn" onClick={handleResetSettings}>
                    ↩️ Reset Settings
                  </button>
                  <button className="action-btn" onClick={() => window.location.reload()}>
                    🔃 Reload App
                  </button>
                </div>
              </section>

              <section className="panel-section">
                <h3>ℹ️ Current Settings</h3>
                <pre className="settings-debug">
                  {JSON.stringify(settings, null, 2)}
                </pre>
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SuperUserPanel;